import React from 'react';
import { Link } from 'react-router-dom';
import { useSEO } from '../hooks/useSEO';
import { getPageByPath, buildPageJsonLd } from '../lib/site';
import { getEvidenceById } from '../lib/evidence';

const page = getPageByPath('/evidencia-brasileira');
const folstein = getEvidenceById('folstein-1975');

const studies = [
  {
    title: 'Brucki et al. (2003)',
    population: '433 indivíduos saudáveis, sem queixas de memória.',
    question: 'Padronização de uso do MEEM no Brasil e influência de variáveis demográficas.',
    finding: 'A escolaridade foi o principal fator associado ao desempenho. Medianas: 20; 25; 26,5; 28; 29 conforme os grupos de escolaridade descritos no estudo.',
    limit: 'Esses valores são medianas observadas, não pontos de corte diagnósticos.',
    href: 'https://doi.org/10.1590/S0004-282X2003000500014',
    n: '433 (185 nos subitens)',
    tipo: 'Referência descritiva'
  },
  {
    title: 'Bertolucci et al. (1994)',
    population: '530 indivíduos de uma população geral brasileira.',
    question: 'Impacto da escolaridade sobre o desempenho e definição de limiares no estudo.',
    finding: 'O estudo definiu valores de corte por escolaridade a partir do percentil 5 inferior da distribuição.',
    limit: 'Os limiares pertencem à população, método e definição operacional daquele estudo; não são universais.',
    href: 'https://doi.org/10.1590/S0004-282X1994000100001',
    n: '530 (+ 94 com comprometimento cognitivo, para comparação)',
    tipo: 'Corte por escolaridade'
  },
  {
    title: 'Lourenço & Veras (2006)',
    population: '303 indivíduos com mais de 65 anos.',
    question: 'Desempenho diagnóstico do MEEM em idosos atendidos em ambiente ambulatorial.',
    finding: 'O estudo avaliou sensibilidade e especificidade de diferentes referências, incluindo o limiar global 23/24.',
    limit: 'A referência é específica para a população idosa estudada e não deve ser extrapolada automaticamente para outras populações.',
    href: 'https://doi.org/10.1590/S0034-89102006000500023',
    n: '303',
    tipo: 'Acurácia diagnóstica'
  }
];

export default function EvidenciaBrasileira() {
  useSEO({ title: page.title, description: page.description, path: page.path });

  return (
    <main className="public-reading max-w-[960px] mx-auto px-4 py-8 md:py-12">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(buildPageJsonLd(page)) }} />
      <nav aria-label="Trilha de navegação" className="mb-8 text-sm text-[var(--text-muted)]">
        <Link to="/">Mini Mental</Link> <span aria-hidden="true">›</span> <Link to="/guias">Guias</Link> <span aria-hidden="true">›</span> Evidência brasileira
      </nav>

      <header className="max-w-[760px] mb-12">
        <p className="text-xs font-semibold text-[var(--text-muted)] mb-3">Proveniência dos números</p>
        <h1 className="text-[var(--primary)] leading-tight mb-5">Evidência brasileira do Mini Mental</h1>
        <p className="text-lg leading-relaxed text-[var(--text-muted)]">As referências brasileiras não são intercambiáveis. Cada estudo responde a uma pergunta, em uma população e com uma definição própria. Esta página mostra de onde vêm os números usados pelo site e onde terminam suas conclusões.</p>
      </header>

      <section aria-labelledby="antes-do-brasil" className="max-w-[760px] mb-12">
        <h2 id="antes-do-brasil" className="text-xl font-bold text-[var(--primary)] mb-3">Por que existe uma evidência brasileira, e não só a original?</h2>
        <p className="text-[var(--text-muted)] leading-relaxed mb-3">O Mini Mental nasceu na publicação original de Folstein, Folstein e McHugh ({folstein.year}), que criou o instrumento e seu método de pontuação. {folstein.population}</p>
        <p className="text-[var(--text-muted)] leading-relaxed">Essa publicação de origem não oferece uma referência de desempenho esperado para a população brasileira: escolaridade, método de alfabetização e o próprio idioma variam entre os países. Por isso, desempenho esperado, pontos de corte e acurácia diagnóstica no Brasil vêm de estudos brasileiros independentes — os três a seguir — não de uma tradução direta do artigo de 1975.</p>
        <a href={`https://doi.org/${folstein.primarySource.value}`} rel="noopener" className="mt-3 inline-flex min-h-11 items-center text-[var(--primary)] hover:underline">Publicação original (Folstein et al., 1975) →</a>
      </section>

      <section aria-labelledby="tabela-comparativa" className="max-w-[760px] mb-12">
        <h2 id="tabela-comparativa" className="text-xl font-bold text-[var(--primary)] mb-3">As três referências, lado a lado</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse text-sm">
            <thead className="bg-[var(--surface-soft)]">
              <tr>
                <th className="p-3 border border-[var(--border)]">Estudo</th>
                <th className="p-3 border border-[var(--border)]">Amostra (n)</th>
                <th className="p-3 border border-[var(--border)]">Tipo de dado</th>
              </tr>
            </thead>
            <tbody>
              {studies.map((study) => (
                <tr key={study.title}>
                  <td className="p-3 border border-[var(--border)] font-bold">{study.title}</td>
                  <td className="p-3 border border-[var(--border)]">{study.n}</td>
                  <td className="p-3 border border-[var(--border)]">{study.tipo}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-sm text-[var(--text-muted)]">Para saber qual dessas referências usar em cada situação clínica, veja <Link to="/pontos-de-corte" className="text-[var(--primary)] hover:underline">pontos de corte do Mini Mental</Link>. Esta página trata só da proveniência dos números, não da escolha entre eles.</p>
      </section>

      <div className="grid gap-6">
        {studies.map((study) => (
          <article key={study.title} className="p-6 md:p-8 bg-[var(--surface)] border border-[var(--border)] rounded-2xl">
            <h2 className="text-2xl font-bold text-[var(--primary)] mb-5">{study.title}</h2>
            <dl className="space-y-4 text-sm leading-relaxed text-[var(--text-muted)]">
              <div><dt className="font-bold text-[var(--primary)]">População</dt><dd>{study.population}</dd></div>
              <div><dt className="font-bold text-[var(--primary)]">Pergunta do estudo</dt><dd>{study.question}</dd></div>
              <div><dt className="font-bold text-[var(--primary)]">O que o site utiliza</dt><dd>{study.finding}</dd></div>
              <div><dt className="font-bold text-[var(--primary)]">Limite</dt><dd>{study.limit}</dd></div>
            </dl>
            <a href={study.href} rel="noopener" className="mt-3 inline-flex min-h-11 items-center text-[var(--primary)] hover:underline">Fonte primária →</a>
          </article>
        ))}
      </div>

      <section className="mt-12 p-6 md:p-8 bg-[var(--surface-soft)] rounded-2xl">
        <h2 className="text-xl font-bold text-[var(--primary)] mb-3">Como isso entra na calculadora?</h2>
        <p className="text-[var(--text-muted)] leading-relaxed mb-4">A ferramenta não funde estudos diferentes em um único “valor correto”. O usuário escolhe a referência e o resultado é contextualizado segundo aquela fonte, com as limitações de população e método preservadas.</p>
        <Link to="/metodologia-da-calculadora" className="inline-flex min-h-11 items-center text-[var(--primary)] hover:underline">Ver metodologia completa →</Link>
      </section>
    </main>
  );
}
